// =============================================================================
// Site-wide "Universe" leaderboard.
//
// Aggregates every entry across every non-hidden, post-bracket-lock league of
// the given tournament type into a single ranked list. Returns only public,
// non-pick data: bracket name, league name, computed point totals. Never
// leaks bonus picks, tiebreakers, real names, or per-match picks.
//
// Scoring reuses the existing per-league pipeline (loadLeaderboardInputs +
// computeLeaderboard) so global totals match what each user sees on their
// league page. Since scoring configs are consistent across leagues of the
// same tournament type in this deployment, raw points compare fairly.
// =============================================================================

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type UniverseRow = {
  entryId: string;
  bracketName: string;
  leagueId: string;
  leagueName: string;
  leagueSlug: string;
  tournamentType: string;
  ownerUserId: string;
  /** Knockout points (bracket picks + goal predictions). Primary rank key. */
  knockoutPoints: number;
  /** Group-stage points; 0 for knockout-only leagues. */
  groupPoints: number;
  /** Bonus-question points earned so far. */
  bonusPoints: number;
  /** All three combined — the number the user sees as their total. */
  totalPoints: number;
  /** Max reachable points from remaining games; secondary tiebreak. */
  totalPossiblePoints: number;
  /** Whether this row's league has already been fully finalized. */
  finalized: boolean;
  /** Global rank, 1-indexed. Ties share the lower rank. */
  rank: number;
};

export type UniverseResponse = {
  rows: UniverseRow[];
  /** entryIds owned by the current viewer (any league, any role). Empty when the viewer has no entries. */
  viewerEntryIds: string[];
  /** Distinct leagues participating in the universe, for context/labels. */
  leagueCount: number;
  entryCount: number;
  /** True when at least one eligible league exists (post-lock). Route redirects home when false. */
  available: boolean;
  /** ISO timestamp of when this snapshot was computed (for the Refresh button). */
  computedAt: string;
};

export const getUniverseLeaderboard = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<UniverseResponse> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { loadLeaderboardInputs } = await import(
      "@/lib/scoring/load-leaderboard-inputs.server"
    );
    const { computeLeaderboard } = await import(
      "@/lib/scoring/compute-leaderboard.server"
    );

    const viewerUserId = context.userId;
    const nowIso = new Date().toISOString();

    // 1) Eligible leagues: non-hidden, bracket lock has passed, in a known
    // tournament type. Bracket-locked ensures we never leak picks — computed
    // rows only expose bracket_name and aggregated points, but the gate is a
    // belt-and-suspenders privacy guarantee.
    const { data: leagues, error: lErr } = await supabaseAdmin
      .from("leagues")
      .select("id, name, slug, tournament_type, bracket_lock_at, is_hidden")
      .eq("is_hidden", false)
      .in("tournament_type", [
        "fifa_wc_mens_knockout",
        "fifa_wc_mens_group_knockout",
      ])
      .not("bracket_lock_at", "is", null)
      .lte("bracket_lock_at", nowIso);
    if (lErr) throw new Error(lErr.message);

    const eligibleLeagues = leagues ?? [];
    if (eligibleLeagues.length === 0) {
      return {
        rows: [],
        viewerEntryIds: [],
        leagueCount: 0,
        entryCount: 0,
        available: false,
        computedAt: nowIso,
      };
    }

    // 2) Compute per-league leaderboards in parallel, then flatten.
    const perLeague = await Promise.all(
      eligibleLeagues.map(async (lg) => {
        const inputs = await loadLeaderboardInputs(lg.id, viewerUserId, {
          skipAuthorization: true,
        });
        const result = computeLeaderboard(inputs);
        return { league: lg, result, inputs };
      }),
    );

    // 3) Flatten to universe rows. `bracketPoints` is the knockout total
    // (post-lock only, so no picks are leaked). `groupPoints` covers group
    // stage for G+K leagues. Bonus is bonus.
    const rows: Omit<UniverseRow, "rank">[] = [];
    const viewerEntryIds = new Set<string>();
    for (const { league, result, inputs } of perLeague) {
      for (const r of result.rows) {
        if (r.ownerUserId === viewerUserId) viewerEntryIds.add(r.entryId);
        rows.push({
          entryId: r.entryId,
          bracketName: r.bracketName,
          leagueId: league.id,
          leagueName: league.name,
          leagueSlug: league.slug,
          tournamentType: league.tournament_type,
          ownerUserId: r.ownerUserId,
          knockoutPoints: r.bracketPoints,
          groupPoints: r.groupPoints,
          bonusPoints: r.bonusPoints,
          totalPoints: r.totalPoints,
          totalPossiblePoints: r.totalPossiblePoints,
          finalized: result.tournamentFinalized,
        });
      }
      // No-op reference to keep TS happy about the returned inputs shape.
      void inputs;
    }

    // 4) Global sort:
    //   1. totalPoints DESC (headline number)
    //   2. knockoutPoints DESC (apples-to-apples across formats when totals tie)
    //   3. totalPossiblePoints DESC (matches per-league leaderboard tiebreak)
    //   4. bracketName ASC (stable)
    rows.sort((a, b) => {
      if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
      if (b.knockoutPoints !== a.knockoutPoints)
        return b.knockoutPoints - a.knockoutPoints;
      if (b.totalPossiblePoints !== a.totalPossiblePoints)
        return b.totalPossiblePoints - a.totalPossiblePoints;
      return a.bracketName.localeCompare(b.bracketName);
    });

    // 5) Assign ranks (dense: ties share the same lower rank).
    const ranked: UniverseRow[] = [];
    let lastKey = "";
    let lastRank = 0;
    rows.forEach((r, idx) => {
      const key = `${r.totalPoints}|${r.knockoutPoints}|${r.totalPossiblePoints}`;
      const rank = key === lastKey ? lastRank : idx + 1;
      lastKey = key;
      lastRank = rank;
      ranked.push({ ...r, rank });
    });

    return {
      rows: ranked,
      viewerEntryIds: Array.from(viewerEntryIds),
      leagueCount: eligibleLeagues.length,
      entryCount: ranked.length,
      available: true,
      computedAt: nowIso,
    };
  });
